/* @flow */
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
    Paper,
    FormControl,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Divider,
    Grid,
    Button,
    Toolbar,
    Typography,
    IconButton,
} from '@mui/material';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import Dialog from '../../components/SharedComponents/Dialog/Dialog';
import HelpButton from '../../components/SharedComponents/HelpButton/HelpButton';
import backIcon from '../../assets/icons/back.png';
import AppLoader from '../../components/AppLoader';
import { getPermissionValue } from '../../helper-classes/utility-functions';
import { saveFleetioAccount, updateFleetioAccount, getFleetioAccount, deleteFleetioAccount } from './helper-classes/fleetio-services';
import { FLEETIO_MESSAGES, FLEETIO_HELP_TEXT } from './constants.fleetio';
import styles from './FleetioConfig.module.scss';

export type Props = {
    history: {
        push: Function,
    },
    onSuccess: Function,
    onCancel: ?Function,
    isEdit: boolean,
};

export type State = {
    submitting: boolean,
    loadingData: boolean,
    apiKey: string,
    accountToken: string,
    odometerSync: boolean,
    engineHourSync: boolean,
    dtcSync: boolean,
    applyForAllAssets: boolean,
    dialogConfig: Object,
    checkboxError: boolean,
};

class FleetioConfig extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            submitting: false,
            loadingData: false,
            apiKey: '',
            accountToken: '',
            odometerSync: false,
            engineHourSync: false,
            dtcSync: false,
            applyForAllAssets: false,
            dialogConfig: { open: false, type: '', customTitle: '', content: '', button: [] },
            checkboxError: false,
        };
    }

    componentDidMount() {
        const { isEdit } = this.props;
        if (isEdit) {
            this.setState({ loadingData: true });
            getFleetioAccount()
                .then((data) => {
                    if (data) {
                        this.setState({
                            apiKey: data.apiKey || '',
                            accountToken: data.token || '',
                            odometerSync: data.odometerSync || false,
                            engineHourSync: data.engineHoursSync || false,
                            dtcSync: data.dtcSync || false,
                        });
                    }
                })
                .finally(() => this.setState({ loadingData: false }));
        }
    }

    handleChange = (e: Object) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    }

    handleCheckbox = (name: string) => {
        this.setState(prev => ({ [name]: !prev[name], checkboxError: false }));
    }

    closeDialog = () => {
        this.setState({ dialogConfig: { open: false, type: '', customTitle: '', content: '', button: [] } });
    }

    applyForAllConfirmation = (nextValue: boolean) => {
        this.setState({
            applyForAllAssets: nextValue,
            dialogConfig: { open: false, type: '', customTitle: '', content: '', button: [] },
        });
    }

    handleApplyForAllClick = (_event: Object, checked: boolean) => {
        if (!checked) {
            this.setState({
                applyForAllAssets: false,
                dialogConfig: { open: false, type: '', customTitle: '', content: '', button: [] },
            });
            return;
        }

        this.setState({
            dialogConfig: {
                open: true,
                type: 'confirm',
                customTitle: '',
                content: FLEETIO_MESSAGES.APPLY_ALL_ASSETS_CONFIRM,
                button: [
                    {
                        title: 'No',
                        color: 'primary',
                        handleClick: () => this.applyForAllConfirmation(false),
                    },
                    {
                        title: 'Yes',
                        color: 'primary',
                        variant: 'contained',
                        handleClick: () => this.applyForAllConfirmation(true),
                    },
                ],
            },
        });
    }

    handleSubmit = () => {
        const {
            apiKey, accountToken, odometerSync, engineHourSync, dtcSync, applyForAllAssets,
        } = this.state;
        if (!odometerSync && !engineHourSync && !dtcSync) {
            this.setState({ checkboxError: true });
            return;
        }
        const { isEdit, onSuccess } = this.props;
        const apiCall = isEdit ? updateFleetioAccount : saveFleetioAccount;
        this.setState({ submitting: true });
        const payload = {
            apiKey,
            token: accountToken,
            odometerSync,
            engineHoursSync: engineHourSync,
            dtcSync,
            ...(isEdit ? { syncPreferences: !!applyForAllAssets } : {}),
        };
        apiCall(payload)
            .then(() => {
                this.setState({
                    dialogConfig: {
                        open: true,
                        type: 'success',
                        customTitle: '',
                        content: isEdit
                            ? FLEETIO_MESSAGES.UPDATE_SUCCESS
                            : FLEETIO_MESSAGES.SAVE_SUCCESS,
                        button: [{ title: 'OK', color: 'primary', variant: 'contained', handleClick: onSuccess }],
                    },
                });
            })
            .catch(() => {
                this.setState({
                    dialogConfig: {
                        open: true,
                        type: 'error',
                        customTitle: FLEETIO_MESSAGES.CREDENTIAL_ERROR_TITLE,
                        content: FLEETIO_MESSAGES.CREDENTIAL_ERROR,
                        button: [{ title: 'OK', color: 'primary', variant: 'contained', handleClick: this.closeDialog }],
                    },
                });
            })
            .finally(() => this.setState({ submitting: false }));
    }

    handleDelete = () => {
        this.setState({
            dialogConfig: {
                open: true,
                type: 'confirm',
                customTitle: FLEETIO_MESSAGES.DELETE_CONFIRM_TITLE,
                content: FLEETIO_MESSAGES.DELETE_CONFIRM,
                button: [
                    { title: 'Cancel', color: 'primary', handleClick: this.closeDialog },
                    { title: 'Delete', color: 'primary', variant: 'contained', handleClick: this.handleDeleteConfirm },
                ],
            },
        });
    }

    handleDeleteConfirm = () => {
        const { history } = this.props;
        this.closeDialog();
        this.setState({ submitting: true });
        deleteFleetioAccount()
            .then(() => {
                history.push('/integrations');
            })
            .catch(() => {
                this.setState({
                    dialogConfig: {
                        open: true,
                        type: 'error',
                        customTitle: '',
                        content: FLEETIO_MESSAGES.DELETE_ERROR,
                        button: [{ title: 'OK', color: 'primary', variant: 'contained', handleClick: this.closeDialog }],
                    },
                });
            })
            .finally(() => this.setState({ submitting: false }));
    }

    render() {
        const { history, onCancel, isEdit } = this.props;
        const handleBack = onCancel || (() => history.push('/integrations'));
        const {
            submitting,
            loadingData,
            apiKey,
            accountToken,
            odometerSync,
            engineHourSync,
            dtcSync,
            applyForAllAssets,
            dialogConfig,
            checkboxError,
        } = this.state;

        return (
            <div className={styles.app}>
                {(loadingData || submitting) && <AppLoader type="fullScreen" />}
                <Paper>
                    <Toolbar className={styles.toolbar}>
                        <span className={styles.backButton}>
                            <IconButton
                                onClick={handleBack}
                                size="large"
                            >
                                <img src={backIcon} alt="backArrow" />
                            </IconButton>
                        </span>
                        <Typography
                            variant="h6"
                            color="inherit"
                            className={styles.titleWrap}
                        >
                            Fleetio Integration
                        </Typography>
                    </Toolbar>
                    <ValidatorForm
                        autoComplete="off"
                        className={styles.form}
                        onSubmit={this.handleSubmit}
                    >
                        <FormControl
                            component="fieldset"
                            className={styles.formControl}
                        >
                            <FormGroup>
                                <Grid
                                    container
                                    direction="column"
                                    justifyContent="space-between"
                                    alignItems="stretch"
                                    className={styles.mainGrid}
                                >
                                    <Grid item xs={8}>
                                        <Typography className={styles.sectionTitle}>
                                            API Credentials
                                        </Typography>
                                        <div className={styles.fieldRow}>
                                            <div className={styles.formField}>
                                                <TextValidator
                                                    name="apiKey"
                                                    label="API Key*"
                                                    value={apiKey}
                                                    fullWidth
                                                    onChange={this.handleChange}
                                                    validators={['required']}
                                                    errorMessages={['This field is required']}
                                                />
                                            </div>
                                            <HelpButton
                                                type="apiKey"
                                                helpText={FLEETIO_HELP_TEXT.API_KEY}
                                                popoverWidth={380}
                                            />
                                        </div>
                                        <div className={styles.fieldRow}>
                                            <div className={styles.formField}>
                                                <TextValidator
                                                    name="accountToken"
                                                    label="Account Token*"
                                                    value={accountToken}
                                                    fullWidth
                                                    onChange={this.handleChange}
                                                    validators={['required']}
                                                    errorMessages={['This field is required']}
                                                />
                                            </div>
                                            <HelpButton
                                                type="accountToken"
                                                helpText={FLEETIO_HELP_TEXT.ACCOUNT_TOKEN}
                                            />
                                        </div>
                                        <Typography className={`${styles.sectionTitle} ${styles.syncTitle}`}>
                                            Sync Preferences<span style={{ color: checkboxError ? 'red' : 'inherit' }}>*</span>
                                            <HelpButton
                                                type="syncPreference"
                                                helpText={FLEETIO_HELP_TEXT.SYNC_PREFERENCE}
                                                popoverWidth={300}
                                            />
                                        </Typography>
                                        <div className={styles.checkboxRow}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={odometerSync}
                                                        onChange={() =>
                                                            this.handleCheckbox('odometerSync')
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label="Primary Meter (Odometer)"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={engineHourSync}
                                                        onChange={() =>
                                                            this.handleCheckbox('engineHourSync')
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label="Secondary Meter (Engine Hours)"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={dtcSync}
                                                        onChange={() =>
                                                            this.handleCheckbox('dtcSync')
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label="DTC (Vehicle Fault Codes)"
                                            />
                                        </div>
                                        {isEdit && (
                                            <div className={styles.checkboxRow}>
                                                <FormControlLabel
                                                    control={(
                                                        <Checkbox
                                                            checked={applyForAllAssets}
                                                            onChange={this.handleApplyForAllClick}
                                                            color="primary"
                                                        />
                                                    )}
                                                    label={(
                                                        <Typography component="span">
                                                            {'Apply to all assets'}
                                                            <span
                                                                className={styles.applyAllHelp}
                                                            >
                                                                <HelpButton
                                                                    type="applyForAllAssets"
                                                                    helpText={
                                                                        FLEETIO_HELP_TEXT
                                                                            .APPLY_FOR_ALL_ASSETS
                                                                    }
                                                                    popoverWidth={330}
                                                                />
                                                            </span>
                                                        </Typography>
                                                    )}
                                                />
                                            </div>
                                        )}
                                        {checkboxError && (
                                            <Typography
                                                color="error"
                                                variant="caption"
                                            >
                                                Please select at least one sync preference.
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </FormGroup>
                        </FormControl>
                        <Divider className={styles.divider} />
                        <Toolbar>
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                <Grid item>
                                    <Button
                                        className={styles.button}
                                        onClick={handleBack}
                                        color="primary"
                                    >
                                        Cancel
                                    </Button>
                                    {getPermissionValue('Admin') === 'Modify' && (
                                        <>
                                            {isEdit && (
                                                <Button
                                                    color="primary"
                                                    className={styles.button}
                                                    onClick={this.handleDelete}
                                                    disabled={submitting}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                className={styles.primaryButton}
                                                type="submit"
                                                disabled={submitting}
                                            >
                                                Save
                                            </Button>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </ValidatorForm>
                </Paper>
                <Dialog
                    open={dialogConfig.open}
                    type={dialogConfig.type}
                    customTitle={dialogConfig.customTitle}
                    button={dialogConfig.button}
                    content={dialogConfig.content}
                    maxWidth="sm"
                />
            </div>
        );
    }
}

export default withRouter(FleetioConfig);
